"use client";

import React, { type Dispatch, type SetStateAction } from "react";

import { useMediaQuery } from "../../../hooks";
import { cn } from "../../../utils";
import { Icon } from "../../Icon";
import { Select, SelectItem } from "../../Select";
import { Typography } from "../../Typography";
import type { TablePaginationType } from "../Table.types";

export interface TablePaginationProps {
  total: number;
  className?: string;
  pagination: TablePaginationType;
  pageSizeOptions?: number[];
  showPageNumbers?: boolean;
  onPaginationChange: Dispatch<SetStateAction<TablePaginationType>>;
}

const getPageNumbers = (
  page: TablePaginationType["page"],
  totalPages: number,
) => {
  const range = (start: number, count: number) =>
    Array.from({ length: count }, (_, i) => start + i);

  return totalPages <= 5
    ? range(1, totalPages)
    : range(Math.max(1, Math.min(page - 2, totalPages - 4)), 5);
};

/**
 * TablePagination is a reusable pagination control for tabular data.
 *
 * This component is intentionally separated from the Table component to:
 * - Reduce the complexity and prop surface of Table by not embedding pagination logic directly.
 * - Avoid forcing Table to accept pagination-specific props like `total`, `onPaginationChange`, and `pageSizeOptions`.
 * - Allow for flexible composition: TablePagination can be used with any data grid or table, not just this Table implementation.
 * - Encourage separation of concerns: Table handles data rendering, while TablePagination manages navigation and page size selection.
 *
 * Use TablePagination alongside Table to keep both components focused, maintainable, and easy to test.
 */
export const TablePagination: React.FC<TablePaginationProps> = ({
  total,
  className,
  pagination: { page, pageSize },
  pageSizeOptions = [5, 10, 20, 50],
  showPageNumbers = true,
  onPaginationChange,
}) => {
  const { isSm } = useMediaQuery();

  const totalPages = Math.ceil(total / pageSize);
  const isFirstPage = page === 1;
  const isLastPage = page === totalPages;
  const isLeftArrowDisabled = isFirstPage || !totalPages;
  const isRightArrowDisabled = isLastPage || !totalPages;
  const pageNumbers = getPageNumbers(page, totalPages);

  return (
    <div
      className={cn(
        "ui:flex ui:items-center ui:justify-end ui:gap-2 ui:sm:gap-4",
        {
          "ui:justify-between": totalPages,
        },
        className,
      )}
    >
      {Boolean(totalPages) && (
        <Typography grotesk size={isSm ? 10 : 12}>
          Page {page} of {totalPages}
        </Typography>
      )}

      <div className="ui:flex ui:items-center ui:gap-4">
        <div className="ui:flex ui:items-center ui:gap-1">
          <Typography grotesk size={isSm ? 10 : 12}>
            Rows per page
          </Typography>
          <Select
            variant="mini"
            value={String(pageSize)}
            defaultValue={String(pageSize)}
            triggerClassName="ui:w-12 ui:text-xs ui:sm:text-xxs ui:h-6"
            onValueChange={(value) => {
              onPaginationChange({ page: 1, pageSize: Number(value) });
            }}
          >
            {pageSizeOptions.map((size) => (
              <SelectItem
                key={size}
                value={String(size)}
                className="ui:h-6 ui:text-xs ui:sm:text-xxs"
              >
                {size}
              </SelectItem>
            ))}
          </Select>
        </div>

        <div className="ui:flex ui:items-center">
          <Icon
            size="xs"
            name="caret-up"
            hoverEffect="circle"
            aria-label="Previous page"
            disabled={isLeftArrowDisabled}
            className={cn("ui:-rotate-90 ui:text-xxs", {
              "ui:opacity-50": isLeftArrowDisabled,
            })}
            onClick={() => {
              onPaginationChange((previous) => ({
                ...previous,
                page: previous.page - 1,
              }));
            }}
          />

          {showPageNumbers &&
            pageNumbers.map((pageNumber) => (
              <button
                key={pageNumber}
                disabled={pageNumber === page}
                className={cn(
                  "ui:cursor-pointer ui:border-b ui:border-divider ui:px-2 ui:py-1 ui:font-grotesk ui:text-xs ui:transition-colors ui:hover:border-primary ui:sm:text-xxs",
                  {
                    "ui:cursor-default ui:border-primary ui:text-textPrimary":
                      pageNumber === page,
                  },
                )}
                onClick={() => {
                  onPaginationChange((previous) => ({
                    ...previous,
                    page: pageNumber,
                  }));
                }}
              >
                {pageNumber}
              </button>
            ))}

          <Icon
            size="xs"
            name="caret-up"
            hoverEffect="circle"
            aria-label="Next page"
            disabled={isRightArrowDisabled}
            className={cn("ui:rotate-90 ui:text-xxs", {
              "ui:opacity-50": isRightArrowDisabled,
            })}
            onClick={() => {
              onPaginationChange((previous) => ({
                ...previous,
                page: previous.page + 1,
              }));
            }}
          />
        </div>
      </div>
    </div>
  );
};
